import {
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  getDocs,
  onSnapshot,
  Timestamp,
  QuerySnapshot
} from 'firebase/firestore';
import type { Unsubscribe, DocumentData } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import type {
  ActivityEvent,
  ActivityEventType,
} from '@/shared/types/activityEvent';

/**
 * Options for querying the activity feed
 */
export interface ActivityFeedOptions {
  /** Number of days to look back (default: 90) */
  timeRange?: number;
  
  /** Maximum number of events to return (default: 500) */
  limit?: number;
  
  /** Filter to specific event types */
  eventTypes?: ActivityEventType[];
  
  /** Include archived events (default: false) */
  includeArchived?: boolean;
}

/**
 * Callback function for real-time activity feed updates
 */
type ActivityFeedCallback = (events: ActivityEvent[]) => void;

/**
 * Callback function for real-time listener errors
 */
type ActivityFeedErrorCallback = (error: Error) => void;

/**
 * Shared service for querying and subscribing to domain activity feeds.
 * Queries activity_log by domain + time range, with optional event type filtering.
 */
class ActivityFeedService {
  /**
   * Get activity feed events, optionally filtered by domain.
   * When domain is omitted or empty, returns events across all domains.
   */
  async getPersonalizedFeed(
    domain: string,
    _userId: string,
    options: ActivityFeedOptions = {}
  ): Promise<ActivityEvent[]> {
    const timeRange = options.timeRange || 90;
    const limit = options.limit || 500;

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeRange);

    const constraints = [
      ...(domain ? [where('domain', '==', domain)] : []),
      where('timestamp', '>=', Timestamp.fromDate(cutoffDate)),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limit),
    ];

    const q = query(
      collection(db, COLLECTION_NAMES.ACTIVITY_LOG),
      ...constraints
    );

    const snapshot = await getDocs(q);
    let events = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }) as ActivityEvent);

    if (options.eventTypes?.length) {
      events = events.filter(e => options.eventTypes!.includes(e.event_type));
    }

    if (!options.includeArchived) {
      events = events.filter(e => !e.event_type.includes('archived'));
    }

    return events;
  }

  /**
   * Subscribe to real-time activity feed updates.
   * When domain is omitted or empty, subscribes to events across all domains.
   */
  subscribeToPersonalizedFeed(
    domain: string,
    _userId: string,
    callback: ActivityFeedCallback,
    options: ActivityFeedOptions = {},
    onError?: ActivityFeedErrorCallback
  ): Unsubscribe {
    const timeRange = options.timeRange || 90;
    const limit = options.limit || 500;

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeRange);

    const constraints = [
      ...(domain ? [where('domain', '==', domain)] : []),
      where('timestamp', '>=', Timestamp.fromDate(cutoffDate)),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limit),
    ];

    const q = query(
      collection(db, COLLECTION_NAMES.ACTIVITY_LOG),
      ...constraints
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        let events = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }) as ActivityEvent);

        if (options.eventTypes?.length) {
          events = events.filter(e => options.eventTypes!.includes(e.event_type));
        }

        if (!options.includeArchived) {
          events = events.filter(e => !e.event_type.includes('archived'));
        }

        callback(events);
      },
      (error) => {
        if (onError) {
          onError(error as Error);
        }
      }
    );

    return unsubscribe;
  }

  /** Get recent activity for a specific resource (division, program, team, or workflow). */
  async getResourceActivity(
    domain: string,
    resourceType: 'division' | 'program' | 'team' | 'workflow',
    resourceId: string,
    limit: number = 50
  ): Promise<ActivityEvent[]> {
    try {
      // Map resource type to context field
      const fieldMap = {
        division: 'context.division_id',
        program: 'context.program_id',
        team: 'context.team_id',
        workflow: 'metadata.affected_workflow_ids'
      };

      const field = fieldMap[resourceType];
      
      // Build appropriate query based on field type
      let q;
      if (field === 'metadata.affected_workflow_ids') {
        // Array-contains for workflow IDs
        q = query(
          collection(db, COLLECTION_NAMES.ACTIVITY_LOG),
          where('domain', '==', domain),
          where(field, 'array-contains', resourceId),
          orderBy('timestamp', 'desc'),
          firestoreLimit(limit)
        );
      } else {
        // Equality for context fields
        q = query(
          collection(db, COLLECTION_NAMES.ACTIVITY_LOG),
          where('domain', '==', domain),
          where(field, '==', resourceId),
          orderBy('timestamp', 'desc'),
          firestoreLimit(limit)
        );
      }

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }) as ActivityEvent);

      return events;

    } catch (error) {
      throw new Error(
        `Failed to get resource activity: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /** Subscribe to real-time activity updates for a specific resource. */
  subscribeToResourceActivity(
    domain: string,
    resourceType: 'division' | 'program' | 'team' | 'workflow',
    resourceId: string,
    callback: ActivityFeedCallback,
    limit: number = 50,
    onError?: ActivityFeedErrorCallback
  ): Unsubscribe {
    // Map resource type to context field
    const fieldMap = {
      division: 'context.division_id',
      program: 'context.program_id',
      team: 'context.team_id',
      workflow: 'metadata.affected_workflow_ids'
    };

    const field = fieldMap[resourceType];
    
    // Build appropriate query based on field type
    let q;
    if (field === 'metadata.affected_workflow_ids') {
      q = query(
        collection(db, COLLECTION_NAMES.ACTIVITY_LOG),
        where('domain', '==', domain),
        where(field, 'array-contains', resourceId),
        orderBy('timestamp', 'desc'),
        firestoreLimit(limit)
      );
    } else {
      q = query(
        collection(db, COLLECTION_NAMES.ACTIVITY_LOG),
        where('domain', '==', domain),
        where(field, '==', resourceId),
        orderBy('timestamp', 'desc'),
        firestoreLimit(limit)
      );
    }

    // Set up snapshot listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const events = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }) as ActivityEvent);
        
        callback(events);
      },
      (error) => {
        console.error('Firestore listener error:', error);
        if (onError) {
          onError(error as Error);
        }
      }
    );

    return unsubscribe;
  }
}

/** Singleton instance of ActivityFeedService. */
export const activityFeedService = new ActivityFeedService();
